import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useProducts } from '../../../hooks/useProducts';

const AdminProdutos = () => {
  const { products, loading, refreshProducts } = useProducts();

  const [saving, setSaving] = useState(false);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    features: '',
    external_link: '',
  });

  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      features: '',
      external_link: '',
    });
    setCoverFile(null);
    setGalleryFiles([]);
    setEditingSlug(null);
  };

  const uploadFile = async (file: File) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${crypto.randomUUID()}.${fileExt}`;
    const filePath = fileName;

    const { error: uploadError } = await supabase.storage
      .from('produtos')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage.from('produtos').getPublicUrl(filePath);
    return data.publicUrl;
  };

  const handleEdit = (product: any) => {
    setEditingSlug(product.slug);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      features: Array.isArray(product.features) ? product.features.join(', ') : '',
      external_link: product.external_link || '',
    });
    setCoverFile(null);
    setGalleryFiles([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (slug: string) => {
    if (!confirm('Tem certeza que deseja excluir este produto?')) return;

    const { error } = await supabase.from('produtos').delete().eq('slug', slug);

    if (error) {
      alert('Erro ao excluir: ' + error.message);
      return;
    }

    if (editingSlug === slug) resetForm();
    refreshProducts();
  };

  const handleToggleVisibility = async (product: any) => {
    const { error } = await supabase
      .from('produtos')
      .update({ visible: product.visible === false ? true : false })
      .eq('slug', product.slug);

    if (error) {
      alert('Erro ao alterar visibilidade: ' + error.message);
      return;
    }

    refreshProducts();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const slug = formData.name
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]+/g, '');

      const features = formData.features
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean);

      if (editingSlug) {
        const updateData: any = {
          name: formData.name,
          slug,
          description: formData.description,
          features,
          external_link: formData.external_link || null,
        };

        if (coverFile) {
          const coverUrl = await uploadFile(coverFile);
          updateData.image = coverUrl;

          if (galleryFiles.length > 0) {
            const galleryUrls: string[] = [];
            for (const file of galleryFiles) {
              const url = await uploadFile(file);
              galleryUrls.push(url);
            }
            updateData.images = [coverUrl, ...galleryUrls];
          }
        } else if (galleryFiles.length > 0) {
          const galleryUrls: string[] = [];
          for (const file of galleryFiles) {
            const url = await uploadFile(file);
            galleryUrls.push(url);
          }

          const currentProduct = products.find((p: any) => p.slug === editingSlug);
          updateData.images = [currentProduct?.image, ...galleryUrls].filter(Boolean);
        }

        const { error } = await supabase
          .from('produtos')
          .update(updateData)
          .eq('slug', editingSlug);

        if (error) throw error;

        alert('Produto atualizado com sucesso!');
      } else {
        if (!coverFile) {
          alert('Selecione a imagem principal.');
          setSaving(false);
          return;
        }

        const coverUrl = await uploadFile(coverFile);

        const galleryUrls: string[] = [];
        for (const file of galleryFiles) {
          const url = await uploadFile(file);
          galleryUrls.push(url);
        }

        const productData = {
          name: formData.name,
          slug,
          description: formData.description,
          image: coverUrl,
          features,
          images: [coverUrl, ...galleryUrls],
          external_link: formData.external_link || null,
          visible: true,
        };

        const { error } = await supabase.from('produtos').insert([productData]);

        if (error) throw error;

        alert('Produto salvo!');
      }

      resetForm();
      refreshProducts();
    } catch (error: any) {
      alert('Erro: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Produtos & Serviços</h1>

      <form onSubmit={handleSubmit} className="space-y-4 mb-10">
        <input
          className="w-full p-2 border rounded"
          placeholder="Nome"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />

        <textarea
          className="w-full p-2 border rounded"
          placeholder="Descrição"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          required
        />

        <div className="border rounded p-3 bg-white">
          <label className="block font-semibold mb-2">
            Imagem principal {editingSlug ? '(opcional para editar)' : ''}
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
            required={!editingSlug}
          />
          {coverFile && <p className="text-sm text-gray-600 mt-2">{coverFile.name}</p>}
        </div>

        <div className="border rounded p-3 bg-white">
          <label className="block font-semibold mb-2">
            Galeria de imagens {editingSlug ? '(opcional na edição)' : ''}
          </label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => setGalleryFiles(Array.from(e.target.files || []))}
          />
          {galleryFiles.length > 0 && (
            <ul className="text-sm text-gray-600 mt-2 space-y-1">
              {galleryFiles.map((file, index) => (
                <li key={index}>{file.name}</li>
              ))}
            </ul>
          )}
        </div>

        <input
          className="w-full p-2 border rounded"
          placeholder="Características (separadas por vírgula)"
          value={formData.features}
          onChange={(e) => setFormData({ ...formData, features: e.target.value })}
        />

        <input
          className="w-full p-2 border rounded"
          placeholder="Link externo (OLX, Mercado Livre, Shopee) - opcional"
          value={formData.external_link}
          onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
        />

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 bg-blue-600 text-white p-3 rounded font-bold"
          >
            {saving
              ? 'Salvando...'
              : editingSlug
              ? 'Atualizar produto'
              : 'Salvar produto'}
          </button>

          {editingSlug && (
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-3 rounded font-bold bg-gray-200"
            >
              Cancelar edição
            </button>
          )}
        </div>
      </form>

      <h2 className="text-xl font-bold mb-4">Produtos cadastrados</h2>

      {loading && <p>Carregando...</p>}

      {!loading && products.length === 0 && <p>Nenhum produto cadastrado.</p>}

      <div className="space-y-4">
        {products.map((product: any) => (
          <div
            key={product.slug}
            className={`border p-4 rounded ${
              product.visible === false ? 'opacity-60 bg-gray-50' : ''
            }`}
          >
            <div className="flex flex-col md:flex-row gap-4">
              <img
                src={product.image}
                className="w-32 h-32 object-cover rounded"
                alt={product.name}
              />

              <div className="flex-1">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-bold text-lg">{product.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                    <p className="text-xs mt-2">
                      Status:{' '}
                      <span className={product.visible === false ? 'text-red-600' : 'text-green-600'}>
                        {product.visible === false ? 'Oculto' : 'Visível'}
                      </span>
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleEdit(product)}
                      className="bg-yellow-500 text-white px-3 py-1 rounded"
                    >
                      Editar
                    </button>

                    <button
                      onClick={() => handleToggleVisibility(product)}
                      className="bg-gray-700 text-white px-3 py-1 rounded"
                    >
                      {product.visible === false ? 'Mostrar' : 'Ocultar'}
                    </button>

                    <button
                      onClick={() => handleDelete(product.slug)}
                      className="bg-red-600 text-white px-3 py-1 rounded"
                    >
                      Excluir
                    </button>
                  </div>
                </div>

                <div className="flex gap-3 mt-3">
                  <a
                    href="https://wa.me/5521992215234"
                    target="_blank"
                    rel="noreferrer"
                    className="bg-green-500 text-white px-3 py-1 rounded"
                  >
                    WhatsApp
                  </a>

                  {product.external_link && (
                    <a
                      href={product.external_link}
                      target="_blank"
                      rel="noreferrer"
                      className="bg-gray-800 text-white px-3 py-1 rounded"
                    >
                      Ver anúncio
                    </a>
                  )}
                </div>

                {Array.isArray(product.images) && product.images.length > 1 && (
                  <div className="flex gap-2 flex-wrap mt-3">
                    {product.images.map((img: string, index: number) => (
                      <img
                        key={index}
                        src={img}
                        alt={`Galeria ${index + 1}`}
                        className="w-20 h-20 object-cover rounded border"
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminProdutos;